import * as React from "react";
import "./orders-transactions.scss";
import { Page } from "components/page/page";
import { Card } from "components/card/card";
import { Order } from "core/domain";
import { Badge, UserLink, CopyablePhone } from "components";
import {
    CopyOutlined,
    DollarCircleOutlined,
    DownOutlined,
    EditOutlined,
    ExportOutlined,
    InfoCircleOutlined,
    ShoppingCartOutlined,
} from "@ant-design/icons";
import {
    confirmAndPayoutOrder,
    createOrderTransactionOrder,
    getAllOrders,
    getAllOrderStateTypes,
    getAllOrderTypes,
} from "../../../../core/repositories/orders-transactions";
import { Button, buttonType } from "components/button/button";
import { Input } from "components/input/input";
// import { ClientSidePagination } from "components/pagination/pagination";
import { Enum, PaginationList } from "../../../../core/domain/commons/common";
import moment from "jalali-moment";
import { digitSperator } from "core/utils/decimalExtentions";
import { Dropdown } from "components/dropdown/dropdown";
import { copyToClipboard } from "core/utils/copyToClipboard";
import { getIconPath } from "core/utils/getIconPath";
import { OrderEditModal } from "./edit-modal/edit-modal";
import { OrderDetailModal } from "./order-detail/order-detail";
import { Modal } from "components/modal/modal";
import DatePicker from "react-multi-date-picker";
import { ReactComponent as CalendarIcon } from "assets/icons/dash-line-icons/calendar.svg";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { downloadCSV } from "../../../../core/utils/export-to-file";
import { getOrderTransactionStateBadgeType } from "./script";
import { ConfirmBulkSelectModal } from "./bulk-select-modal/bulk-select-modal";
import {
    Dropdown as AntdDropdown,
    Empty,
    Menu,
    Table as AntdTable,
    Tag,
    Tooltip,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { jFormat } from "core/utils/jFormat";
import { getTransactionPlatforms } from "core/repositories/transaction";

interface FilterValues {
    search: string;
    state: Enum;
    type?: Enum;
    platform?: Enum;
    source?: Enum;
    startDate: string;
    endDate: string;
}

const empty_filters = {
    search: "",
    state: {
        id: -1,
        title: "همه وضعیت ها",
    },
    type: {
        id: -1,
        title: "همه ",
    },
    platform: {
        id: -1,
        title: "همه پلتفرم ها",
    },
    source: {
        id: -1,
        title: "همه مقصدها",
    },
    startDate: moment()
        .locale("fa")
        .subtract(1, "months")
        .format("jYYYY/jMM/jDD"),
    endDate: "",
};

// OrderTransactionDestinationSource در بک‌اند
const orderSources: Enum[] = [
    empty_filters.source,
    { id: 1, title: "کیف پول" },
    { id: 2, title: "حساب بانکی" },
    { id: 3, title: "CashIn" },
];

const actionItems: Enum[] = [
    {
        id: 0,
        title: "انتخاب عملیات",
    },
    {
        id: 100,
        title: "تایید و تسویه",
    },
];

export const OrdersTransactions: React.FC = () => {
    const [detailModalIsOpen, setDetailModalIsOpen] =
        React.useState<boolean>(false);

    const startDateRef = React.useRef();
    const endDateRef = React.useRef();

    const [selectedItem, setSelectedItem] = React.useState<Order>();
    const [confirmButtonLoading, setConfirmButtonLoading] =
        React.useState<boolean>(false);
    const [query, setQuery] = React.useState<string>("");
    const [editModalIsOpen, setEditModalIsOpen] =
        React.useState<boolean>(false);
    const [orderTypes, setOrderTypes] = React.useState<Enum[]>([]);
    const [orderStateTypes, setOrderStateTypes] = React.useState<Enum[]>([
        {
            id: -1,
            title: "همه وضعیت",
        },
    ]);
    const [orderPlatforms, setOrderPlatforms] = React.useState<Enum[]>([
        empty_filters.platform,
    ]);
    const [orders, setOrders] = React.useState<PaginationList<Order>>();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [buttonLoading, setButtonLoading] = React.useState<boolean>(false);
    const [deleteButtonLoading, setDeleteButtonLoading] =
        React.useState<boolean>(false);
    const [payoutModalIsOpen, setPayoutModalIsOpen] =
        React.useState<boolean>(false);
    const [createOrderModalIsOpen, setCreateOrderModalIsOpen] =
        React.useState<boolean>(false);
    const [createOrderButtonLoading, setCreateOrderButtonLoading] =
        React.useState<boolean>(false);
    const [filterValues, setFilterValues] =
        React.useState<FilterValues>(empty_filters);
    const [pageSize, setPageSize] = React.useState<number>(10);
    const [selectedRows, setSelectedRows] = React.useState<Order[]>();
    const [confirmBulkActionModalIsOpen, setConfirmBulkActionModalIsOpen] =
        React.useState<boolean>(false);
    const [actionApproveLoading, setActionApproveLoading] =
        React.useState<boolean>(false);
    const [selectedActionItem, setSelectedActionItem] = React.useState<Enum>(
        actionItems[0],
    );

    const clearFiltersHandle = () => {
        setFilterValues({ ...empty_filters, startDate: "" });
        filterHandle(true);
    };

    const filterHandle = (clearFilter?: boolean) => {
        var _query = "";

        if (filterValues.search)
            _query += `${_query.includes("?") ? "&" : "?"}keywords=${filterValues.search}`;
        if (filterValues.type?.id !== -1)
            _query += `${_query.includes("?") ? "&" : "?"}type=${filterValues.type?.id}`;
        if (filterValues.state?.id !== -1)
            _query += `${_query.includes("?") ? "&" : "?"}state=${filterValues.state?.id}`;
        if (filterValues.platform?.id !== -1)
            _query += `${_query.includes("?") ? "&" : "?"}platform=${filterValues.platform?.id}`;
        if (filterValues.source && filterValues.source.id !== -1)
            _query += `${_query.includes("?") ? "&" : "?"}source=${filterValues.source.id}`;
        if (filterValues.startDate !== "")
            _query += `${_query.includes("?") ? "&" : "?"}startDate=${jFormat(filterValues.startDate)}`;
        if (filterValues.endDate !== "")
            _query += `${_query.includes("?") ? "&" : "?"}endDate=${jFormat(filterValues.endDate)}`;

        const appliedQuery = clearFilter ? "" : _query;
        setQuery(appliedQuery);

        getAllOrders(
            clearFilter
                ? setDeleteButtonLoading
                : _query !== ""
                  ? setButtonLoading
                  : setLoading,
            setOrders,
            1,
            pageSize,
            appliedQuery,
        );
    };

    React.useEffect(() => {
        confirmBulkActionModalIsOpen === false && setSelectedRows(undefined);
    }, [confirmBulkActionModalIsOpen]);

    React.useEffect(() => {
        const fetchInitialData = async () => {
            // این‌ها فقط گزینه‌های فیلتر را پر می‌کنند؛ اگر setLoading را به آن‌ها
            // بدهیم، اسپینر جدول چند بار پشت سر هم روشن/خاموش می‌شود.
            const ignoreLoading = () => {};
            await getAllOrderTypes(ignoreLoading, setOrderTypes);
            await getAllOrderStateTypes(ignoreLoading, setOrderStateTypes);
            getTransactionPlatforms(ignoreLoading, setOrderPlatforms);

            const initialQuery = `?startDate=${jFormat(empty_filters.startDate)}`;
            setQuery(initialQuery);
            getAllOrders(setLoading, setOrders, 1, 10, initialQuery);
        };

        fetchInitialData();
    }, []);

    const onPayoutIconClickHandle = React.useCallback((item) => {
        setSelectedItem(item);
        setPayoutModalIsOpen(true);
    }, []);

    const onCreateOrderIconClickHandle = React.useCallback((item) => {
        setSelectedItem(item);
        setCreateOrderModalIsOpen(true);
    }, []);

    const openActionConfirmModal = () => {
        setActionApproveLoading(false);
        setConfirmBulkActionModalIsOpen(true);
    };

    const handleTableChange = React.useCallback(
        (pagination: TablePaginationConfig) => {
            const nextPage = pagination.current ?? 1;
            const nextPageSize = pagination.pageSize ?? pageSize;
            const currentPage = orders?.pagination.pageIndex ?? 1;

            if (nextPage === currentPage && nextPageSize === pageSize) return;

            setPageSize(nextPageSize);
            setSelectedRows(undefined);
            getAllOrders(setLoading, setOrders, nextPage, nextPageSize, query);
        },
        [orders?.pagination.pageIndex, pageSize, query],
    );

    const columns = React.useMemo<ColumnsType<Order>>(
        () => [
            {
                title: "شناسه",
                dataIndex: "orderTransactionTrackingCode",
                key: "orderTransactionTrackingCode",
                width: 190,
                render: (trackingCode: string) => (
                    <span className="en copiable">
                        {trackingCode}
                        <Tooltip title="کپی شناسه سفارش">
                            <button
                                type="button"
                                className="table-action-button copy-button"
                                aria-label="کپی شناسه سفارش"
                                onClick={() =>
                                    copyToClipboard(trackingCode, "شناسه سفارش")
                                }
                            >
                                <CopyOutlined />
                            </button>
                        </Tooltip>
                    </span>
                ),
            },
            {
                title: "نام",
                dataIndex: "fullName",
                key: "fullName",
                width: 150,
                render: (fullName: string, row) => (
                    <UserLink userId={row.userId}>{fullName || "-"}</UserLink>
                ),
            },
            {
                title: "موبایل",
                dataIndex: "phoneNumber",
                key: "phoneNumber",
                width: 135,
                render: (phoneNumber: string, row) => (
                    <CopyablePhone phoneNumber={phoneNumber} />
                ),
            },
            {
                title: "جفت ارز",
                key: "pair",
                width: 170,
                render: (_, row) => (
                    <div className="cell pair">
                        <img
                            src={getIconPath(row.sourceSymbol.toLowerCase())}
                            alt=""
                        />
                        <span className="en">{row.sourceSymbol}</span>
                        <span aria-hidden="true"> / </span>
                        <span className="en">{row.destinationSymbol}</span>
                        <img
                            src={getIconPath(
                                row.destinationSymbol.toLowerCase(),
                            )}
                            alt=""
                        />
                    </div>
                ),
            },
            {
                title: "مبلغ مبدا",
                dataIndex: "sourceAmount",
                key: "sourceAmount",
                align: "left",
                width: 130,
                render: (amount: number, row) =>
                    row.sourceSymbol.toLowerCase() === "irt"
                        ? digitSperator(amount.toFixed(0))
                        : amount,
            },
            {
                title: "مبلغ مقصد",
                dataIndex: "destinationAmount",
                key: "destinationAmount",
                align: "left",
                width: 130,
                render: (amount: number, row) =>
                    row.destinationSymbol.toLowerCase() === "irt"
                        ? digitSperator(amount.toFixed(0))
                        : amount,
            },
            {
                title: "تاریخ ثبت سفارش",
                dataIndex: "createdOn",
                key: "createdOn",
                width: 175,
                render: (createdOn: string) => (
                    <span>
                        {moment.utc(createdOn).local().format("HH:mm")}{" "}
                        {moment
                            .utc(createdOn)
                            .locale("fa")
                            .local()
                            .format("YYYY/MM/DD")}
                    </span>
                ),
            },
            {
                title: "وضعیت",
                dataIndex: "state",
                key: "state",
                width: 150,
                render: (state: Enum) => (
                    <Badge
                        text={state.title}
                        type={getOrderTransactionStateBadgeType(state?.id)}
                    />
                ),
            },
            {
                title: "نوع سفارش",
                dataIndex: "type",
                key: "type",
                width: 130,
                render: (type: Enum) => (
                    <Badge
                        text={type.title}
                        type={type.title === "خرید" ? "success" : "error"}
                    />
                ),
            },
            {
                title: "پلتفرم",
                dataIndex: "platform",
                key: "platform",
                width: 125,
                render: (platform?: Enum) =>
                    platform ? (
                        <Tag color={platform.id === 1 ? "blue" : "green"}>
                            {platform.title}
                        </Tag>
                    ) : (
                        "-"
                    ),
            },
            {
                title: "عملیات",
                key: "action",
                fixed: "left",
                width: 175,
                render: (_, row) => (
                    <div className="actions">
                        <Tooltip title="ویرایش معامله">
                            <button
                                type="button"
                                className="table-action-button"
                                aria-label="ویرایش معامله"
                                onClick={() => {
                                    setSelectedItem(row);
                                    setEditModalIsOpen(true);
                                }}
                            >
                                <EditOutlined />
                            </button>
                        </Tooltip>
                        <Tooltip title="تایید و تسویه">
                            <button
                                type="button"
                                className="table-action-button payout-action"
                                aria-label="تایید و تسویه"
                                onClick={() => onPayoutIconClickHandle(row)}
                            >
                                <DollarCircleOutlined />
                            </button>
                        </Tooltip>
                        {/* فقط برای معامله‌ای که سفارشش ساخته نشده و واریز موفق دارد */}
                        {row.hasOrder === false && row.hasSucceededDeposit === true && (
                            <Tooltip title="ساخت سفارش">
                                <button
                                    type="button"
                                    className="table-action-button create-order-action"
                                    aria-label="ساخت سفارش"
                                    onClick={() =>
                                        onCreateOrderIconClickHandle(row)
                                    }
                                >
                                    <ShoppingCartOutlined />
                                </button>
                            </Tooltip>
                        )}
                        <Tooltip title="جزئیات معامله">
                            <button
                                type="button"
                                className="table-action-button"
                                aria-label="جزئیات معامله"
                                onClick={() => {
                                    setSelectedItem(row);
                                    setDetailModalIsOpen(true);
                                }}
                            >
                                <InfoCircleOutlined />
                            </button>
                        </Tooltip>
                    </div>
                ),
            },
        ],
        [onPayoutIconClickHandle, onCreateOrderIconClickHandle],
    );
    return (
        <Page className="pmx-order-transactions" title="مدیریت معاملات">
            <Modal
                setIsOpen={setPayoutModalIsOpen}
                isOpen={payoutModalIsOpen}
                className="confirm-payout-modal"
            >
                <p>
                    آیا از تایید و تسویه تراکنش کاربر {selectedItem?.fullName}{" "}
                    اطمینان دارید؟
                </p>

                <div className="buttons">
                    <Button
                        type={buttonType.info}
                        text="بله"
                        onClick={() =>
                            confirmAndPayoutOrder(
                                setConfirmButtonLoading,
                                {
                                    trackingCode:
                                        selectedItem?.orderTransactionTrackingCode,
                                },
                                () => setPayoutModalIsOpen(false),
                                () =>
                                    getAllOrders(
                                        setLoading,
                                        setOrders,
                                        orders?.pagination.pageIndex ?? 1,
                                        pageSize,
                                        query,
                                    ),
                            )
                        }
                        loading={confirmButtonLoading}
                    />
                    <Button
                        type={buttonType.error}
                        text="خیر"
                        onClick={() => setPayoutModalIsOpen(false)}
                    />
                </div>
            </Modal>
            <Modal
                setIsOpen={setCreateOrderModalIsOpen}
                isOpen={createOrderModalIsOpen}
                className="confirm-payout-modal create-order-modal"
            >
                <p>
                    آیا از ساخت سفارش برای معامله{" "}
                    <span className="en">
                        {selectedItem?.orderTransactionTrackingCode}
                    </span>{" "}
                    کاربر {selectedItem?.fullName} اطمینان دارید؟
                </p>

                <div className="buttons">
                    <Button
                        type={buttonType.info}
                        text="بله"
                        onClick={() =>
                            createOrderTransactionOrder(
                                setCreateOrderButtonLoading,
                                {
                                    trackingCode:
                                        selectedItem?.orderTransactionTrackingCode,
                                },
                                () => setCreateOrderModalIsOpen(false),
                                () =>
                                    getAllOrders(
                                        setLoading,
                                        setOrders,
                                        orders?.pagination.pageIndex ?? 1,
                                        pageSize,
                                        query,
                                    ),
                            )
                        }
                        loading={createOrderButtonLoading}
                        disabled={createOrderButtonLoading}
                    />
                    <Button
                        type={buttonType.error}
                        text="خیر"
                        onClick={() => setCreateOrderModalIsOpen(false)}
                    />
                </div>
            </Modal>
            {selectedRows && (
                <ConfirmBulkSelectModal
                    isOpen={confirmBulkActionModalIsOpen}
                    setIsOpen={setConfirmBulkActionModalIsOpen}
                    buttonLoading={actionApproveLoading}
                    selectedActionItem={selectedActionItem}
                    selectedRows={selectedRows}
                    setButtonLoading={setActionApproveLoading}
                    setSelectedRows={setSelectedRows}
                    fetchData={async () => {
                        await getAllOrders(
                            setLoading,
                            setOrders,
                            orders?.pagination.pageIndex ?? 1,
                            pageSize,
                            query,
                        );
                    }}
                    actionItem={selectedActionItem}
                />
            )}
            {/* اسپینر فقط روی خود جدول است؛ blur روی Card باعث دو اسپینر هم‌زمان می‌شد. */}
            <Card title="معاملات">
                {selectedItem && (
                    <OrderDetailModal
                        title="جزئیات معامله"
                        selectedRow={selectedItem}
                        isOpen={detailModalIsOpen}
                        setIsOpen={setDetailModalIsOpen}
                        trackingId={selectedItem?.orderTransactionTrackingCode}
                    />
                )}
                {selectedItem && orderStateTypes && (
                    <OrderEditModal
                        orderStates={orderStateTypes}
                        isOpen={editModalIsOpen}
                        setIsOpen={setEditModalIsOpen}
                        item={selectedItem}
                        onClose={() =>
                            getAllOrders(
                                setLoading,
                                setOrders,
                                1,
                                pageSize,
                                query,
                            )
                        }
                    />
                )}
                <div className="filter-row">
                    <div className="fields">
                        <Input
                            value={filterValues.search}
                            className="search"
                            placeholder="جستجو کنید ..."
                            onPressEnter={filterHandle}
                            name="search"
                            onChange={(e) =>
                                setFilterValues({
                                    ...filterValues,
                                    search: e.target.value,
                                })
                            }
                        />

                        {orderTypes.length > 0 && (
                            <Dropdown
                                items={orderTypes}
                                onChangeHandle={(value) =>
                                    setFilterValues({
                                        ...filterValues,
                                        type: orderTypes.find(
                                            (l) => l.id === value,
                                        ),
                                    })
                                }
                                selectedOption={filterValues.type}
                            />
                        )}
                        {orderStateTypes.length > 0 && (
                            <Dropdown
                                items={orderStateTypes}
                                onChangeHandle={(value) =>
                                    setFilterValues({
                                        ...filterValues,
                                        state: orderStateTypes.find(
                                            (l) => l.id === value,
                                        ),
                                    })
                                }
                                selectedOption={filterValues.state}
                            />
                        )}
                        {orderPlatforms.length > 0 && (
                            <Dropdown
                                items={orderPlatforms}
                                onChangeHandle={(value) =>
                                    setFilterValues({
                                        ...filterValues,
                                        platform: orderPlatforms.find(
                                            (l) => l.id === value,
                                        ),
                                    })
                                }
                                selectedOption={filterValues.platform}
                            />
                        )}
                        <Dropdown
                            items={orderSources}
                            onChangeHandle={(value) =>
                                setFilterValues({
                                    ...filterValues,
                                    source: orderSources.find((l) => l.id === value),
                                })
                            }
                            selectedOption={filterValues.source}
                        />

                        <DatePicker
                            className="date-picker"
                            value={filterValues.startDate}
                            calendar={persian}
                            ref={startDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.startDate}
                                        icon={<CalendarIcon />}
                                        placeholder="از تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({
                                    ...filterValues,
                                    startDate: e.format(),
                                });
                            }}
                        />
                        <DatePicker
                            className="date-picker"
                            value={filterValues.endDate}
                            calendar={persian}
                            ref={endDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.endDate}
                                        icon={<CalendarIcon />}
                                        placeholder="تا تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({
                                    ...filterValues,
                                    endDate: e.format(),
                                });
                            }}
                        />
                    </div>
                    <div className="buttons">
                        <Button
                            text="خروجی اکسل"
                            type={buttonType.info}
                            icon={<ExportOutlined />}
                            onClick={() =>
                                downloadCSV("معاملات", orders?.items ?? [])
                            }
                        />
                        <Button
                            loading={buttonLoading}
                            disabled={buttonLoading}
                            text="اعمال فیلتر"
                            type={buttonType.success}
                            onClick={() => filterHandle()}
                        />
                        <Button
                            loading={deleteButtonLoading}
                            disabled={deleteButtonLoading}
                            text="حذف فیلتر"
                            type={buttonType.error}
                            onClick={clearFiltersHandle}
                        />
                    </div>
                </div>
                {selectedRows && selectedRows?.length > 1 && (
                    <div className="action-row">
                        <label htmlFor="">عملیات: </label>
                        <AntdDropdown
                            trigger={["click", "hover"]}
                            overlay={
                                <Menu>
                                    {actionItems &&
                                        actionItems.map((state) => (
                                            <Menu.Item
                                                onClick={() =>
                                                    setSelectedActionItem(state)
                                                }
                                            >
                                                {state.title}
                                            </Menu.Item>
                                        ))}
                                </Menu>
                            }
                            placement="bottom"
                        >
                            <Button
                                icon={<DownOutlined />}
                                type={buttonType.outlined}
                                text={selectedActionItem.title}
                            />
                        </AntdDropdown>
                        <Button
                            disabled={selectedActionItem.id === 0}
                            type={buttonType.info}
                            text="اصلاح تراکنش‌های انتخاب شده"
                            onClick={() => openActionConfirmModal()}
                        />
                        <Button
                            type={buttonType.outlined}
                            text={`${selectedRows?.length} آیتم انتخاب شده`}
                        />
                    </div>
                )}
                <AntdTable<Order>
                    className="orders-transactions-table"
                    rowKey="orderTransactionTrackingCode"
                    columns={columns}
                    dataSource={orders?.items ?? []}
                    loading={loading}
                    locale={{
                        emptyText: <Empty description="رکوردی یافت نشد" />,
                    }}
                    rowSelection={{
                        selectedRowKeys:
                            selectedRows?.map(
                                (order) => order.orderTransactionTrackingCode,
                            ) ?? [],
                        onChange: (_, selected) => setSelectedRows(selected),
                    }}
                    scroll={{ x: 1660 }}
                    pagination={{
                        current: orders?.pagination.pageIndex ?? 1,
                        pageSize: orders?.pagination.pageSize ?? pageSize,
                        total: orders?.pagination.totalCount ?? 0,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 20, 50, 100, 1000, 10000, 100000],
                        showTotal: (total, range) =>
                            `نمایش ${digitSperator(range[0])} تا ${digitSperator(range[1])} از ${digitSperator(total)} رکورد`,
                    }}
                    onChange={handleTableChange}
                />
            </Card>
        </Page>
    );
};
